import type {
    BidListItemModel,
    PowerOfAttorneyModel,
    WaybillBidMatchingLogViewModel,
} from "api";
import { BidStatus } from "api";
import { Transition } from "components/shared/column";
import ExternalPrintFormDownloadBtn from "components/shared/externalPrintFormDownloadBtn";
import { Select } from "components/shared/form";
import InlineBtn from "components/shared/inlineBtn";
import Loader from "components/shared/loader";
import QrCodesDownloadBtn from "components/shared/qrCodesDownloadBtn";
import { bidSelectLabel } from "helpers/bid";
import { dateTimeToView } from "helpers/date";
import {
    waybillTitleStatuses,
    waybillTitleStatusesEnum,
    waybillTitleTypes,
    waybillTitleTypesEnum,
} from "helpers/enums";
import { paths } from "helpers/paths";
import { canDownloadExternalPrintForms } from "helpers/printForms";
import { viewContainer } from "helpers/redux";
import { enumLabel } from "helpers/system";
import type { RolesModel } from "helpers/roles";
import {
    canAcceptWaybillTitleByLogist,
    canArchiveWaybills,
    canDownloadWaybillQrCodes,
    canSignWaybillResponseTitle,
    hasSignedNextWaybillTitle,
    isWaybillTitleActionBlocked,
    isWaybillDeliveryExternal,
    isWaybillTitleSignatureInvalid,
    isWaybillTitleSigningRetry,
    nextSignedWaybillTitleWarning,
} from "helpers/waybills";
import { useExternalPrintFormDownload } from "hooks/useExternalPrintFormDownload";
import React, { FC, useCallback, useEffect, useState } from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { Link } from "react-router-dom";
import { getBidMainData } from "sources/books/bids";
import { getPrintFormFromExternal } from "sources/ebills";
import type { WaybillTitleModel, WaybillTitleStatus, WaybillViewModel } from "sources/waybills";
import { applyBid, getAvailableBids, getItem, getMatchingLogs } from "sources/waybills";
import { StructuredWaybillContent, WaybillContentView } from "./content";
import { WaybillGeneralInfo } from "./generalInfo";
import { useWaybillArchiveOperation, WaybillArchiveAction, WaybillArchiveBadge } from "./archiveStatusAction";
import { WaybillMatchingHistory } from "./matchingHistory";
import { WaybillTitleRejection, WaybillTitleSignatureWarning, WaybillTitleStatusBadge } from "./titleRejection";
import { WaybillExternalWarnings } from "./externalWarnings";
import { WaybillTitleFileView } from "./titleFileView";

const responseTitleTypeBySource = {
    [waybillTitleTypesEnum.T1]: waybillTitleTypesEnum.T2,
    [waybillTitleTypesEnum.T3]: waybillTitleTypesEnum.T4,
};
const driverAcceptableTitleTypes = [waybillTitleTypesEnum.T1, waybillTitleTypesEnum.T3];
const responseAllowedSourceStatuses = [waybillTitleStatusesEnum.Accepted, waybillTitleStatusesEnum.Processed];
const failedTitleStatuses = [waybillTitleStatusesEnum.Failed, waybillTitleStatusesEnum.FailedToSend];
const terminalTitleStatuses: WaybillTitleStatus[] = [waybillTitleStatusesEnum.Sent, waybillTitleStatusesEnum.Processed];
const signingUnavailableTooltip = (
    <>
        Подписание титула доступно, когда заявка <br />
        находится в статусе „Начата“ или „Завершена“
    </>
);

/** Return the newest title of the requested type. */
const latestTitleByType = (titles: WaybillTitleModel[], type: WaybillTitleModel["type"]) =>
    titles.reduce<WaybillTitleModel | undefined>(
        (latest, title) => (title.type === type && (!latest || title.id > latest.id) ? title : latest),
        undefined,
    );

/** Describe whether T8 will be created or the current draft will be regenerated. */
const changeTitleButtonLabel = (t8Title?: WaybillTitleModel) =>
    isWaybillTitleSignatureInvalid(t8Title)
        ? "Повторно сформировать и подписать Т8"
        : t8Title && !terminalTitleStatuses.includes(t8Title.status)
          ? "Повторить формирование Т8"
          : "Сформировать Т8";

interface WaybillShowProps {
    match: { params: { id: string } };
    history: { push: (path: string) => void };
    modal: (params: {}) => void;
    mergeParams: (params: {}) => void;
    roles: RolesModel;
    profile: { powerOfAttorneys?: PowerOfAttorneyModel[]; availableFeatureModules: string[] };
}

interface WaybillTitlesProps {
    data: WaybillViewModel;
    bidStatus: BidStatus | null;
    modal: WaybillShowProps["modal"];
    powerOfAttorneys?: PowerOfAttorneyModel[];
    reload: () => void;
}

interface WaybillBidSelectProps {
    inputProps: { name: string };
    url: (params: {}) => Promise<BidListItemModel[]>;
    propName: (bid: BidListItemModel) => string;
    filterPropName: string;
    minimumInputLength: number;
    placeholder: string;
    value: number | null;
    onChange: (payload: { value: number | null }) => void;
    disabled: boolean;
}

const WaybillBidSelect = Select as unknown as FC<WaybillBidSelectProps>;

const titleStatusLabel = (title: WaybillTitleModel) => {
    if (driverAcceptableTitleTypes.includes(title.type as (typeof driverAcceptableTitleTypes)[number])) {
        return title.signed ? "Подтвержден водителем" : "Ожидает подтверждения водителем";
    }
    if (title.signed) {
        return "Подписан";
    }
    if (title.status === waybillTitleStatusesEnum.Failed) {
        return "Ошибка формирования";
    }
    if (title.status === waybillTitleStatusesEnum.FailedToSend) {
        return "Ошибка отправки";
    }
    return enumLabel(waybillTitleStatuses, title.status, title.status);
};

export const WaybillTitles = ({ data, bidStatus, modal, powerOfAttorneys, reload }: WaybillTitlesProps) => {
    const externalDelivery = isWaybillDeliveryExternal(data.intStateFlags);
    const signingDisabled = bidStatus !== BidStatus.Started && bidStatus !== BidStatus.Done;
    const signingTooltip = signingDisabled ? signingUnavailableTooltip : undefined;

    const openAcceptTitle = (title: WaybillTitleModel) => {
        modal({
            name: "acceptWaybillTitle",
            title:
                title.status === waybillTitleStatusesEnum.FailedToSend
                    ? `Повторная отправка замечаний по ${title.type}`
                    : `Подтверждение ${enumLabel(waybillTitleTypes, title.type)} от имени водителя`,
            windowMod: "wide",
            waybillId: data.id,
            waybillTitle: title,
            callback: reload,
        });
    };

    const openGenerateResponseTitle = (sourceTitle: WaybillTitleModel, retry: boolean) => {
        const responseType = responseTitleTypeBySource[sourceTitle.type as keyof typeof responseTitleTypeBySource];
        modal({
            name: "signWaybillResponseTitle",
            title: retry
                ? `Повторное формирование и подписание ${responseType} ЭТрН`
                : `Формирование и подписание ${responseType} ЭТрН`,
            windowMod: "wide",
            waybillId: data.id,
            sourceTitleId: sourceTitle.id,
            powerOfAttorneys,
            callback: reload,
        });
    };

    const openGenerateChangeTitle = () => {
        modal({
            name: "generateWaybillChangeTitle",
            title: "Формирование и подписание Т8 ЭТрН",
            windowMod: "wide",
            waybillId: data.id,
            powerOfAttorneys,
            callback: reload,
        });
    };

    const openSignTitle = (title: WaybillTitleModel) => {
        const retrySend = title.status === waybillTitleStatusesEnum.FailedToSend;
        modal({
            name: "signFile",
            title: isWaybillTitleSignatureInvalid(title)
                ? `Повторное подписание ${title.type} ЭТрН`
                : retrySend
                  ? `Повторная отправка ${title.type} ЭТрН`
                  : `Подписание ${title.type} ЭТрН`,
            windowMod: "wide",
            waybillId: data.id,
            titleId: title.id,
            callback: reload,
        });
    };

    const hasT3 = data.titles.some((t) => t.type === waybillTitleTypesEnum.T3);
    const changeTitleRequired = !externalDelivery && !hasT3 && data.changeTitleRequired;
    const t8Title = latestTitleByType(data.titles, waybillTitleTypesEnum.T8);
    const canDownloadQrCodes = canDownloadWaybillQrCodes(data.titles);

    return (
        <section className="card mb-4">
            <div className="card-header py-2 font-weight-bold d-flex justify-content-between align-items-center">
                <span>Титулы</span>
                {changeTitleRequired && (
                    <InlineBtn
                        value={changeTitleButtonLabel(t8Title)}
                        cname="usb-key"
                        action={openGenerateChangeTitle}
                        disabled={signingDisabled && !isWaybillTitleSignatureInvalid(t8Title)}
                        tooltip={isWaybillTitleSignatureInvalid(t8Title) ? undefined : signingTooltip}
                    />
                )}
            </div>
            <WaybillExternalWarnings waybill={data} className="px-3 pt-3" />
            {changeTitleRequired && (
                <div className="alert alert-warning m-3 mb-0 d-flex align-items-center justify-content-between">
                    <span>Требуется сформировать Т8 ЭТрН (замена водителя / ТС)</span>
                </div>
            )}
            {data.titles.length === 0 ? (
                <div className="card-body text-muted">Титулы отсутствуют</div>
            ) : (
                <div className="card-body p-0">
                    {data.titles.map((title, index) => {
                        const actionBlocked = externalDelivery || isWaybillTitleActionBlocked(title, data.titles);
                        const nextTitleSigned = hasSignedNextWaybillTitle(title, data.titles);
                        const responseType =
                            responseTitleTypeBySource[title.type as keyof typeof responseTitleTypeBySource];
                        const responseTitle = responseType ? latestTitleByType(data.titles, responseType) : undefined;
                        const canAcceptByLogist = !externalDelivery && canAcceptWaybillTitleByLogist(title, data.titles);
                        const retryRemarks = title.status === waybillTitleStatusesEnum.FailedToSend;
                        const canGenerateResponse =
                            !!responseType &&
                            !actionBlocked &&
                            responseAllowedSourceStatuses.includes(
                                title.status as (typeof responseAllowedSourceStatuses)[number],
                            ) &&
                            (!responseTitle || !terminalTitleStatuses.includes(responseTitle.status));
                        const canSignResponse = !actionBlocked && canSignWaybillResponseTitle(title, data.titles);
                        const canRegenerateT8 =
                            !actionBlocked &&
                            title.type === waybillTitleTypesEnum.T8 &&
                            isWaybillTitleSignatureInvalid(title) &&
                            title.id === t8Title?.id &&
                            !changeTitleRequired;
                        return (
                            <div
                                className={`p-3 font-size-14 ${index < data.titles.length - 1 ? "border-bottom" : ""}`}
                                key={title.id}
                            >
                                <div className="d-flex align-items-center justify-content-between">
                                    <strong>{enumLabel(waybillTitleTypes, title.type, title.type)}</strong>
                                    <WaybillTitleStatusBadge
                                        title={title}
                                        label={titleStatusLabel(title)}
                                        failed={failedTitleStatuses.includes(
                                            title.status as (typeof failedTitleStatuses)[number],
                                        )}
                                    />
                                </div>
                                <div className="mt-2">
                                    <WaybillTitleRejection rejectionReason={title.rejectionReason} />
                                    <WaybillTitleSignatureWarning title={title} />
                                    <div>
                                        <span className="text-muted">Получен:</span> {dateTimeToView(title.receivedAt)}
                                    </div>
                                    {title.signedAt && !isWaybillTitleSignatureInvalid(title) && (
                                        <div>
                                            <span className="text-muted">Подписан:</span>{" "}
                                            {dateTimeToView(title.signedAt)}
                                        </div>
                                    )}
                                    {title.remarks && Object.keys(title.remarks).length > 0 && (
                                        <div className="mt-2">
                                            <div className="font-weight-bold mb-1">Замечания</div>
                                            <StructuredWaybillContent
                                                content={title.remarks}
                                                path={`title-${title.id}`}
                                            />
                                        </div>
                                    )}
                                    {externalDelivery && <WaybillTitleFileView waybillId={data.id} title={title} />}
                                    {(canAcceptByLogist ||
                                        canGenerateResponse ||
                                        canSignResponse ||
                                        canRegenerateT8) && (
                                        <div className="d-flex justify-content-end mt-2">
                                            {canAcceptByLogist && (
                                                <InlineBtn
                                                    value={
                                                        retryRemarks
                                                            ? "Повторить отправку замечаний"
                                                            : "Подтвердить от имени водителя"
                                                    }
                                                    cname="checkmark"
                                                    action={() => openAcceptTitle(title)}
                                                    disabled={(signingDisabled && !retryRemarks) || nextTitleSigned}
                                                    tooltip={
                                                        nextTitleSigned
                                                            ? nextSignedWaybillTitleWarning
                                                            : retryRemarks
                                                              ? undefined
                                                              : signingTooltip
                                                    }
                                                />
                                            )}
                                            {canGenerateResponse && (
                                                <InlineBtn
                                                    value={
                                                        responseTitle
                                                            ? isWaybillTitleSignatureInvalid(responseTitle)
                                                                ? `Повторно сформировать и подписать ${responseType}`
                                                                : `Повторить формирование и подписание ${responseType}`
                                                            : `Сформировать и подписать ${responseType}`
                                                    }
                                                    cname="usb-key"
                                                    action={() => openGenerateResponseTitle(title, !!responseTitle)}
                                                    disabled={
                                                        signingDisabled &&
                                                        !isWaybillTitleSignatureInvalid(responseTitle)
                                                    }
                                                    tooltip={
                                                        isWaybillTitleSignatureInvalid(responseTitle)
                                                            ? undefined
                                                            : signingTooltip
                                                    }
                                                />
                                            )}
                                            {canRegenerateT8 && (
                                                <InlineBtn
                                                    value={changeTitleButtonLabel(title)}
                                                    cname="usb-key"
                                                    action={openGenerateChangeTitle}
                                                />
                                            )}
                                            {canSignResponse && (
                                                <InlineBtn
                                                    value={
                                                        isWaybillTitleSignatureInvalid(title)
                                                            ? "Подписать повторно"
                                                            : title.status === waybillTitleStatusesEnum.FailedToSend
                                                              ? "Повторить отправку"
                                                              : "Подписать"
                                                    }
                                                    cname="usb-key"
                                                    action={() => openSignTitle(title)}
                                                    disabled={signingDisabled && !isWaybillTitleSigningRetry(title)}
                                                    tooltip={
                                                        signingDisabled && !isWaybillTitleSigningRetry(title)
                                                            ? signingUnavailableTooltip
                                                            : undefined
                                                    }
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {data.bidId && canDownloadQrCodes && (
                <div className="card-footer d-flex justify-content-end py-2">
                    <QrCodesDownloadBtn bidId={data.bidId} waybillId={data.id} />
                </div>
            )}
        </section>
    );
};

const WaybillShow = (props: WaybillShowProps) => {
    const { mergeParams } = props;
    const [data, setData] = useState<WaybillViewModel | null>(null);
    const [selectedBidId, setSelectedBidId] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [savingBid, setSavingBid] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [bidStatus, setBidStatus] = useState<BidStatus | null>(null);
    const [matchingLogs, setMatchingLogs] = useState<WaybillBidMatchingLogViewModel[] | null>(null);
    const [matchingLogsLoading, setMatchingLogsLoading] = useState(false);
    const [matchingLogsError, setMatchingLogsError] = useState(false);
    const { download: downloadPrintForm, isDownloading: isPrintFormDownloading } = useExternalPrintFormDownload({
        documentType: "waybill",
        requestFn: getPrintFormFromExternal,
    });
    const waybillId = Number(props.match.params.id);

    const loadMatchingLogs = useCallback(() => {
        setMatchingLogsLoading(true);
        setMatchingLogsError(false);
        return getMatchingLogs(waybillId)
            .then((response) => {
                if (response === false) {
                    setMatchingLogsError(true);
                    return;
                }
                setMatchingLogs(Array.isArray(response) ? response : []);
            })
            .catch(() => {
                setMatchingLogsError(true);
            })
            .finally(() => setMatchingLogsLoading(false));
    }, [waybillId]);

    const loadData = useCallback(
        (updateRegistry = false) => {
            setLoading(true);
            setLoadError(false);
            setBidStatus(null);
            return getItem(waybillId)
                .then(async (response) => {
                    if (!response) {
                        setData(null);
                        setLoadError(true);
                        return;
                    }
                    const bid = response.bidId
                        ? await getBidMainData(String(response.bidId)).catch(() => false)
                        : false;
                    setBidStatus(bid ? bid.status : null);
                    setData(response);
                    setSelectedBidId(response.bidId || null);
                    if (updateRegistry) {
                        mergeParams({});
                    }
                })
                .catch(() => {
                    setData(null);
                    setLoadError(true);
                })
                .finally(() => setLoading(false));
        },
        [waybillId, mergeParams],
    );
    const archiveOperation = useWaybillArchiveOperation(() => loadData(true));

    useEffect(() => {
        loadData();
        loadMatchingLogs();
    }, [loadData, loadMatchingLogs]);

    useEffect(() => {
        if (window.location.hash === "#matching-history") {
            const timer = setTimeout(() => {
                const element = document.getElementById("matching-history");
                if (element) {
                    element.scrollIntoView({ behavior: "smooth" });
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [loading, matchingLogsLoading]);

    const updateBid = (bidId: number | null) => {
        setSavingBid(true);
        return applyBid({ waybillId, bidId })
            .then((success) => {
                if (success) {
                    loadMatchingLogs();
                    return loadData(true);
                }
            })
            .finally(() => setSavingBid(false));
    };

    const unlinkBid = () =>
        props.modal({
            name: "confirm",
            title: "Отвязать заявку от ЭТрН?",
            text: "Связь с текущей заявкой будет удалена.",
            buttons: { confirm: "Отвязать", cancel: "Отмена" },
            action: () => updateBid(null),
        });

    const renderHead = () => (
        <div className="book-show__head">
            <button
                type="button"
                className="book-show__close close close--sm border-0 bg-transparent"
                onClick={() => props.history.push(paths.waybills)}
                aria-label="Вернуться к реестру ЭТрН"
            />
            <div className="book-show__title">
                ЭТрН {data?.content.billNumber || `№${waybillId}`}
                {data && <WaybillArchiveBadge intStateFlags={data.intStateFlags} />}
            </div>
            {data && (
                <div className="book-show__head-actions">
                    {canDownloadExternalPrintForms(props.roles) && (
                        <div className="mr-3">
                            <ExternalPrintFormDownloadBtn
                                documentType="waybill"
                                loading={isPrintFormDownloading(data.id)}
                                onClick={() => downloadPrintForm(data.id)}
                            />
                        </div>
                    )}
                    <WaybillArchiveAction
                        waybillId={data.id}
                        intStateFlags={data.intStateFlags}
                        canArchive={canArchiveWaybills(props.roles, props.profile.availableFeatureModules)}
                        modal={props.modal}
                        operation={archiveOperation}
                    />
                </div>
            )}
        </div>
    );

    const renderBidLink = () => {
        if (!data) {
            return null;
        }
        const changed = selectedBidId !== (data.bidId || null);
        return (
            <section className="card mb-4">
                <div className="card-header py-2 font-weight-bold">Связанная заявка</div>
                <div className="card-body">
                    <div className="mb-2">
                        Текущая связь:{" "}
                        {data.bidId ? <Link to={paths.bidShow("bid", data.bidId)}>Заявка №{data.bidId}</Link> : "нет"}
                    </div>
                    <div>
                        <label className="mb-1" htmlFor="waybillBidIdInput">
                            Заявка
                        </label>
                        <div className="d-flex">
                            <div className="flex-1 pr-3">
                                <WaybillBidSelect
                                    inputProps={{ name: "waybillBidId" }}
                                    url={getAvailableBids}
                                    propName={bidSelectLabel}
                                    filterPropName="id"
                                    minimumInputLength={1}
                                    placeholder="Введите номер заявки"
                                    value={selectedBidId}
                                    onChange={({ value }: { value: number | null }) => setSelectedBidId(value)}
                                    disabled={savingBid}
                                />
                            </div>
                            <div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-brand mr-2"
                                    disabled={!changed || savingBid || selectedBidId === null}
                                    onClick={() => updateBid(selectedBidId)}
                                >
                                    {savingBid ? "Сохранение..." : data.bidId ? "Перепривязать" : "Привязать"}
                                </button>
                                {data.bidId && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        disabled={savingBid}
                                        onClick={unlinkBid}
                                    >
                                        Отвязать
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="small text-muted mt-2">
                        Доступны неудалённые заявки в статусах «Черновик», «Запланирована», «Начата» и «Выполнена».
                    </div>
                </div>
            </section>
        );
    };

    return (
        <div className="page-content">
            <Transition dir="full">
                <div className="page-book__show book-show">
                    {renderHead()}
                    <div className="book-show__body">
                        <div className="native-scroll__wrap h-100">
                            <Scrollbars>
                                <div className="native-scroll__container py-3">
                                    {loading ? (
                                        <Loader />
                                    ) : loadError || !data ? (
                                        <div className="alert alert-danger" role="alert">
                                            Не удалось открыть ЭТрН. Проверьте доступ или попробуйте обновить страницу.
                                        </div>
                                    ) : (
                                        <>
                                            <WaybillGeneralInfo carLogist={data.carLogist} />
                                            {renderBidLink()}
                                            <WaybillTitles
                                                data={data}
                                                bidStatus={bidStatus}
                                                modal={props.modal}
                                                powerOfAttorneys={props.profile.powerOfAttorneys}
                                                reload={() => loadData(true)}
                                            />
                                            <section className="card mb-4" id="matching-history">
                                                <div className="card-header py-2 font-weight-bold">
                                                    История сопоставления
                                                </div>
                                                <div className="card-body">
                                                    <WaybillMatchingHistory
                                                        logs={matchingLogs}
                                                        loading={matchingLogsLoading}
                                                        error={matchingLogsError}
                                                        reload={loadMatchingLogs}
                                                    />
                                                </div>
                                            </section>
                                            <WaybillContentView content={data.content} />
                                        </>
                                    )}
                                </div>
                            </Scrollbars>
                        </div>
                    </div>
                </div>
            </Transition>
        </div>
    );
};

export default viewContainer(WaybillShow as unknown as FC, "waybills");
